import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import _ from "underscore";
import "react-day-picker/style.css";
import { DayPicker } from "react-day-picker";
import { ptBR, enUS } from "react-day-picker/locale";

import styles from "./dashboard.module.css";
import { educationInstitution } from "../../../services/education";
import { financeInstitution } from "../../../services/finance"
import { listFiles } from "../../../services/import";
import { getFavoriteDashboard } from "../../../services/preferences";

import { useInstitution } from "../../../context/InstitutionContext";

import {
  pluralize,
  formatDate,
  addPorcentage,
  formatNumberToCurrency
} from "../../../utils/formatter";
import useOutsideAlerter from "../../../utils/clickOutside";

import Button from "../../../components/button/button";
import ZeroData from "../../../components/zeroData/zeroData";
import TeacherTable from "../../../components/education/teacherTable/teacherTable";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import DashboardIcon from "../../../assets/icons/dashboard-icon.svg";
import FinanceCard from "../../../components/finance/financeCard/financeCard";

import DisciplineIndexTable from "../../../components/insights/disciplineIndexTable/disciplineIndexTable";
import TicketMedioCursoTable from "../../../components/insights/ticketMedioCursoTable/ticketMedioCursoTable";

import CalendarIcon from "../../../assets/icons/calendar.svg";
import loadingGif from "../../../assets/loading.gif";
import FinanceInvoicing from "../../../components/finance/financeInvoicing/financeInvoicing"
import BarsFilterIcon from "../../../assets/icons/bars-filter.svg";
import AcademicIcon from "../../../assets/icons/academic-icon-bg.svg";
import DocumentArrowRight from "../../../assets/icons/document-arrow-right.svg";
import FinanceDeliquency from "../../../components/finance/financeDeliquency/financeDeliquency"
import RegistrationConcluseIcon from "../../../assets/icons/registration-concluse.svg";

import ChartMixedPorcentageIcon from "../../../assets/icons/chart-mixed-porcentage.svg";
import ChartMixedUpCircleDollarIcon from "../../../assets/icons/chart-mixed-up-circle-dollar.svg";
import ChartMixedDownCircleDollarIcon from "../../../assets/icons/chart-mixed-down-circle-dollar.svg";

import ChartUserCheckIcon from "../../../assets/icons/chart-user-check.svg";
import ChartUserSearchIcon from "../../../assets/icons/chart-user-search.svg";
import EnvelopeOpenDollarIcon from "../../../assets/icons/envelope-open-dollar.svg";
import MagnifyingGlassIcon from "../../../assets/icons/magnifying-glass-blue.svg";

import ChartUserShield from "../../../assets/icons/chart-user-shield.svg";
import DocumentCheckedFilled from "../../../assets/icons/document-checked-filled.svg";
import ChartMixedRightCircleDollar from "../../../assets/icons/chart-mixed-right-circle-dollar.svg";
import EducationDebtsPeriod from "../../../components/education/educationDebtsPeriod/educationDebtsPeriod";
import { set } from "react-hook-form";
import { getDateRange, saveDateRange } from "../../../utils/localStorage";

const Dashboard = () => {
  const { institution } = useInstitution();
  const [hasFiles, setHasFiles] = useState(undefined);
  const [checkingFavorite, setCheckingFavorite] = useState(true);

  const calendarRef = useRef(null);

  const navigate = useNavigate();

  // Verificar se há um dashboard favorito e redirecionar
  useEffect(() => {
    const checkFavoriteDashboard = async () => {
      try {
        const favoriteDashboardId = await getFavoriteDashboard();
        if (favoriteDashboardId) {
          navigate(`/app/dashboard-customizado/${favoriteDashboardId}`, { replace: true });
          return;
        }
      } catch (error) {
        console.error("Erro ao verificar dashboard favorito:", error);
      } finally {
        setCheckingFavorite(false);
      }
    };

    checkFavoriteDashboard();
  }, [navigate]);

  const [education, setEducation] = useState(null);
  const [finance, setFinance] = useState(null);
  const [ageGroup, setAgeGroup] = useState({})
  const [loading, setLoading] = useState(false)
  const [suggestionRange, setSuggestionRange] = useState("");
  const [customMonths, setCustomMonths] = useState("24");
  const [customYears, setCustomYears] = useState("7");
  const [dateRangeString, setDateRangeString] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    return getDateRange() || { from: new Date(new Date().getFullYear(), 0, 1), to: new Date() };
  });

  useEffect(() => {
    setDateRangeString(
      `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
    );
  }, [dateRange]);

  // Persistir data no localStorage para compartilhar entre telas
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      saveDateRange(dateRange);
    }
  }, [dateRange]);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useOutsideAlerter(calendarRef, () => setIsCalendarOpen(false));

  useEffect(() => {
    if (hasFiles !== true || !institution?.id) return;

    fetchInstitutionEducation({
      institutionId: institution.id,
      startDate: formatDate(dateRange?.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange?.to).replaceAll("/", "-")
    });

    fetchInstitutionFinance({
      institutionId: institution.id,
      startDate: formatDate(dateRange?.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange?.to).replaceAll("/", "-"),
      filtershift: undefined
    });
  }, [
    hasFiles,
    dateRange,
    institution?.id
  ]);

  useEffect(() => {
    if (hasFiles === false) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [hasFiles]);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const files = await listFiles(institution?.id);
        setHasFiles(!!(files && files.length));
      } catch (error) {
        console.error("Erro ao buscar arquivos: ", error);
        setHasFiles(false);
      }
    };

    if (institution?.id) {
      fetchFiles();
    }
  }, [institution]);

  const fetchInstitutionEducation = useCallback(
    _.debounce(async ({ institutionId, startDate, endDate }) => {
      try {
        setLoading(true)
        const response = await educationInstitution({
          institutionId,
          startDate,
          endDate
        });

        setEducation(response.educationData);
        const ageGroup = {
          ageGroup_18_25: Number((response.educationData.ageGroup_18_25 || 0).toFixed(2)),
          ageGroup_26_35: Number((response.educationData.ageGroup_26_35 || 0).toFixed(2)),
          ageGroup_36_45: Number((response.educationData.ageGroup_36_45 || 0).toFixed(2)),
          moreThan40: Number((response.educationData.ageGroup_46Plus || 0).toFixed(2))
        }
        setAgeGroup(ageGroup)
      } catch (error) {
        console.error("Erro ao buscar dados financeiros:", error);
      } finally {
        setLoading(false);
      }
    }, 1000),
    []
  );

  const fetchInstitutionFinance = useCallback(
    _.debounce(async ({ institutionId, startDate, endDate, filtershift }) => {
      try {
        setLoading(true);
        const response = await financeInstitution({
          institutionId,
          startDate,
          endDate,
          filtershift
        });
        const financeData = response.financeData || response;
        
        setFinance(financeData);
        setIsCalendarOpen(false);
      } catch (error) {
        console.error("Erro ao buscar dados financeiros:", error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }, 1000),
    []
  );

  const selectManualRange = () => {
    setSuggestionRange("");

    if (hasFiles !== true || !institution?.id) return;

    fetchInstitutionEducation({
      institutionId: institution.id,
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-")
    });
    fetchInstitutionFinance({
      institutionId: institution.id,
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      filtershift: undefined
    });
  };

  const selectSuggestionRange = (suggestionRange) => {
    setSuggestionRange(suggestionRange);

    if (hasFiles !== true) return;
    const currentYear = new Date().getFullYear();

    let selectedRange;
    switch (suggestionRange) {
      case "today":
        selectedRange = {
          from: new Date(),
          to: new Date()
        };
        break;
      case "last_7":
        selectedRange = {
          from: new Date(new Date().setDate(new Date().getDate() - 7)),
          to: new Date()
        };
        break;
      case "last_30":
        const last_30_now = new Date();
        selectedRange = {
          from: new Date(last_30_now.getFullYear(), last_30_now.getMonth(), 1),
          to: new Date(last_30_now.getFullYear(), last_30_now.getMonth() + 1, 0)
        };
        break;
      case "first_semester":
        selectedRange = {
          from: new Date(`${currentYear}-01-01T00:00:00`),
          to: new Date(`${currentYear}-06-30T23:59:59`)
        };
        break;

      case "second_semester":
        selectedRange = {
          from: new Date(`${currentYear}-07-01T00:00:00`),
          to: new Date(`${currentYear}-12-31T23:59:59`)
        };
        break;

      case "last_semester":
        const today_ls = new Date();
        const isSecondSemester = today_ls.getMonth() >= 6; // Julho = mês 6 (0-indexed)
        
        if (isSecondSemester) {
          // Estamos no segundo semestre, último semestre = primeiro semestre do ano atual
          selectedRange = {
            from: new Date(`${currentYear}-01-01T00:00:00`),
            to: new Date(`${currentYear}-06-30T23:59:59`)
          };
        } else {
          // Estamos no primeiro semestre, último semestre = segundo semestre do ano anterior
          const lastYear = currentYear - 1;
          selectedRange = {
            from: new Date(`${lastYear}-07-01T00:00:00`),
            to: new Date(`${lastYear}-12-31T23:59:59`)
          };
        }
        break;

      case "last_year":
        const now_ly = new Date();
        selectedRange = {
          from: new Date(now_ly.getFullYear() - 1, now_ly.getMonth(), 1),
          to: new Date(now_ly.getFullYear(), now_ly.getMonth(), 0)
        };
        break;

      case "last_x_months":
        const monthsBack = parseInt(customMonths, 10);
        if (!isNaN(monthsBack) && monthsBack > 0) {
          const now_lxm = new Date();
          selectedRange = {
            from: new Date(now_lxm.getFullYear(), now_lxm.getMonth() - monthsBack, now_lxm.getDate()),
            to: new Date()
          };
        } else {
          return;
        }
        break;

      case "last_5_years":
        selectedRange = {
          from: new Date(new Date().setFullYear(new Date().getFullYear() - 5)),
          to: new Date()
        };
        break;

      case "last_x_years":
        const yearsBack = parseInt(customYears, 10);
        if (!isNaN(yearsBack) && yearsBack > 0) {
          const startYear = currentYear - yearsBack;
          selectedRange = {
            from: new Date(`${startYear}-01-01T00:00:00`),
            to: new Date(`${currentYear - 1}-12-31T23:59:59`)
          };
        } else {
          return;
        }
        break;

      case "current_year":
        selectedRange = {
          from: new Date(currentYear, 0, 1),
          to: new Date()
        };
        break;

      case "previous_year":
        const previousYear = currentYear - 1;
        selectedRange = {
          from: new Date(`${previousYear}-01-01T00:00:00`),
          to: new Date(`${previousYear}-12-31T23:59:59`)
        };
        break;

      default:
        selectedRange = {
          from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
          to: new Date(new Date().setMonth(new Date().getMonth() - 0))
        };
        break;
    }

    setDateRange(selectedRange);
    setDateRangeString(
      `${formatDate(selectedRange.from)} - ${formatDate(selectedRange.to)}`
    );
    setIsCalendarOpen(false);

    if (!institution?.id) return;

    fetchInstitutionEducation({
      institutionId: institution.id,
      startDate: formatDate(selectedRange.from).replaceAll("/", "-"),
      endDate: formatDate(selectedRange.to).replaceAll("/", "-")
    });
    fetchInstitutionFinance({
      institutionId: institution.id,
      startDate: formatDate(selectedRange.from).replaceAll("/", "-"),
      endDate: formatDate(selectedRange.to).replaceAll("/", "-"),
      filtershift: undefined
    });
  };

  const handleClearSelection = () => {
    setSuggestionRange("");
    setDateRange({
      from: new Date(),
      to: new Date()
    });
  };

  const buildDataLinkFromFilters = (queryName) => {
    if (!finance?.appliedFilters) return null;
    
    const filterData = finance.appliedFilters.find(f => f.query === queryName);
    if (!filterData || !filterData.predicates) return null;

    const params = new URLSearchParams({ page: '1' });
    let filterIndex = 0;
    const groupId = 0;

    const collectedFilters = [];

    filterData.predicates.forEach((predicate) => {
      const singleMatch = predicate.match(/(\w+)\.(\w+)\s*=\s*'([^']+)'/);
      if (singleMatch) {
        const field = singleMatch[2];
        const value = singleMatch[3];
        collectedFilters.push({ field, operator: 'eq', values: [value] });
        return;
      }

      const inMatch = predicate.match(/(\w+)\.(\w+)\s+IN\s*\(([^)]+)\)/);
      if (inMatch) {
        const field = inMatch[2];
        const valuesStr = inMatch[3];
        const values = valuesStr.split(',').map(v => v.trim().replace(/'/g, ''));
        
        let filteredValues = values;
        if (queryName === 'DelinquencyValue' && field === 'AccountReceiveStatus') {
          filteredValues = ['RECEBIDO', 'VENCIDO', 'EM ABERTO'];
        }
        
        if (filteredValues.length > 0) {
          collectedFilters.push({ field, operator: 'in', values: filteredValues });
        }
      }
    });

    if (queryName === 'DelinquencyValue' && dateRange?.to) {
      const endDate = dateRange.to;
      const formattedDate = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      collectedFilters.push({ field: 'DueDate', operator: 'lt', values: [formattedDate] });
    }

    const filterOrder = ['AccountReceiveStatus', 'DueDate', 'BillType'];
    collectedFilters.sort((a, b) => {
      const indexA = filterOrder.indexOf(a.field);
      const indexB = filterOrder.indexOf(b.field);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return 0;
    });

    collectedFilters.forEach((filter, index) => {
      params.append(`filter${filterIndex}_field`, filter.field);
      params.append(`filter${filterIndex}_operator`, filter.operator);
      params.append(`filter${filterIndex}_values`, JSON.stringify(filter.values));
      params.append(`filter${filterIndex}_groupId`, groupId.toString());
      if (filterIndex > 0) {
        params.append(`filter${filterIndex}_condition`, 'AND');
      }
      filterIndex++;
    });

    return `/app/dados-consolidados?${params.toString()}`;
  };

  const mockedFinance = {
    financialData: {
      totalRevenue: 1000,
      totalExpenses: 1750,
      profitMargin: 25,
      defaultRate: 8,
    },
    billed: [
      { month: "2024-11", value: 180_000 },
      { month: "2024-12", value: 195_000 },
      { month: "2025-01", value: 210_000 },
      { month: "2025-02", value: 200_000 },
      { month: "2025-03", value: 205_000 },
      { month: "2025-04", value: 215_000 },
    ],
    proportionReceivedIssued: [
      83.37,
      83.25,
      86.48,
      81.15,
      84.33,
      85.3
    ],
    received: [
      { month: "2024-11", value: 165_000 },
      { month: "2024-12", value: 176_000 },
      { month: "2025-01", value: 189_000 },
      { month: "2025-02", value: 181_000 },
      { month: "2025-03", value: 187_000 },
      { month: "2025-04", value: 196_000 },
    ],
    monthlyDelinquencyRates: [
      { month: "2024-11", delinquencyRate: 6.5 },
      { month: "2024-12", delinquencyRate: 6.9 },
      { month: "2025-01", delinquencyRate: 7.2 },
      { month: "2025-02", delinquencyRate: 7.6 },
      { month: "2025-03", delinquencyRate: 7.9 },
      { month: "2025-04", delinquencyRate: 8.1 },
    ],

    averageTicket: 340,
  };

  const mockedDisciplineIndex = [
    { courseTitle: "Medicina", ticketMedio: 2150.30 },
    { courseTitle: "Engenharia de Software", ticketMedio: 1850.75 },
    { courseTitle: "Arquitetura e Urbanismo", ticketMedio: 1680.45 },
    { courseTitle: "Odontologia", ticketMedio: 1590.80 },
    { courseTitle: "Engenharia Civil", ticketMedio: 1450.60 },
    { courseTitle: "Direito", ticketMedio: 1350.00 },
    { courseTitle: "Psicologia", ticketMedio: 1200.50 },
    { courseTitle: "Fisioterapia", ticketMedio: 1150.25 },
    { courseTitle: "Economia", ticketMedio: 1100.80 },
    { courseTitle: "Enfermagem", ticketMedio: 1050.40 },
    { courseTitle: "Administração", ticketMedio: 950.75 },
    { courseTitle: "Contabilidade", ticketMedio: 920.30 },
    { courseTitle: "Marketing", ticketMedio: 890.25 },
    { courseTitle: "Pedagogia", ticketMedio: 850.15 },
    { courseTitle: "Letras", ticketMedio: 780.90 },
    { courseTitle: "História", ticketMedio: 750.60 },
    { courseTitle: "Geografia", ticketMedio: 720.45 },
    { courseTitle: "Matemática", ticketMedio: 695.30 },
    { courseTitle: "Ciências Biológicas", ticketMedio: 680.75 },
    { courseTitle: "Educação Física", ticketMedio: 650.50 }
  ];

  const mockedEducation = {
    totalEnrolledStudents: 320,
  };
  // Mostrar loading enquanto verifica dashboard favorito
  if (checkingFavorite) {
    return (
      <div className={styles.dashboardWrapper}>
        <HeaderTitle title="Dashboard" icon={DashboardIcon} />
        <div className={styles.dashboardBody}>
          <div className={styles.financeContent}>
            <div className={styles.loadingWrapper}>
              <img src={loadingGif} alt="Carregando..." />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardWrapper}>
      <HeaderTitle title="Dashboard" icon={DashboardIcon}>
        <div className={styles.headerTitleActions}>
          <div className={styles.headerTitleActionsCalendar}>
            <Button
              icon={CalendarIcon}
              text={dateRangeString}
              size="normal"
              variation="outline"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />

            {isCalendarOpen && (
              <div 
                ref={calendarRef} 
                className={styles.calenderContent}
              >
                <div className={styles.suggestions}>
                  <ul>
                    <li
                      onClick={() => !loading && selectSuggestionRange("today")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "today"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Hoje
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_7")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_7"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 7 dias
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_30")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_30"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Último mês
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("first_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "first_semester"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Primeiro Semestre
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("second_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "second_semester"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Segundo Semestre
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_semester"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Último Semestre
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_year"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 12 meses
                    </li>
                    <li
                      className={clsx(styles.suggestionsItem, styles.customMonthsItem, {
                        [styles.active]: suggestionRange === "last_x_months"
                      })}
                      style={{ opacity: loading ? 0.6 : 1 }}
                    >
                      <span>Últimos</span>
                      <input
                        type="number"
                        min="1"
                        className={styles.customMonthsInput}
                        value={customMonths}
                        placeholder="24"
                        onChange={(e) => setCustomMonths(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !loading && customMonths) {
                            selectSuggestionRange("last_x_months");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={loading}
                      />
                      <span
                        style={{ cursor: loading || !customMonths ? 'default' : 'pointer' }}
                        onClick={() => {
                          if (!loading && customMonths) {
                            selectSuggestionRange("last_x_months");
                          }
                        }}
                      >
                        meses
                      </span>
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("current_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "current_year"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Ano Atual
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("previous_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "previous_year"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Ano Anterior
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_5_years")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_5_years"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 5 Anos
                    </li>
                    <li
                      className={clsx(styles.suggestionsItem, styles.customMonthsItem, {
                        [styles.active]: suggestionRange === "last_x_years"
                      })}
                      style={{ opacity: loading ? 0.6 : 1 }}
                    >
                      <span>Últimos</span>
                      <input
                        type="number"
                        min="1"
                        className={styles.customMonthsInput}
                        value={customYears}
                        placeholder="7"
                        onChange={(e) => setCustomYears(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !loading && customYears) {
                            selectSuggestionRange("last_x_years");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={loading}
                      />
                      <span
                        style={{ cursor: loading || !customYears ? 'default' : 'pointer' }}
                        onClick={() => {
                          if (!loading && customYears) {
                            selectSuggestionRange("last_x_years");
                          }
                        }}
                      >
                        anos
                      </span>
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("manual")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "manual"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Personalizado
                    </li>
                  </ul>
                  <div>
                    <div
                      onClick={() => !loading && handleClearSelection()}
                      className={clsx(styles.suggestionsItem, styles.active)}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Limpar seleção
                    </div>
                    <div
                      onClick={() => !loading && selectManualRange()}
                      className={clsx(
                        styles.suggestionsItem,
                        styles.active,
                        styles["btn-search-data"]
                      )}
                      style={{ 
                        opacity: loading ? 0.8 : 1, 
                        cursor: loading ? 'not-allowed' : 'pointer' 
                      }}
                    >
                      {loading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <img
                            src={loadingGif}
                            alt="Carregando..."
                            style={{ width: "16px", height: "16px" }}
                          />
                          <span>Carregando...</span>
                        </div>
                      ) : (
                        <>
                          <img src={MagnifyingGlassIcon} alt="magnifying-glass" />
                          Buscar dados
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.dateRangeGroup}>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Início</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.from}
                      defaultMonth={dateRange?.from}
                      onSelect={(from) => setDateRange((prev) => ({ ...prev, from }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Fim</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.to}
                      defaultMonth={dateRange?.to}
                      onSelect={(to) => setDateRange((prev) => ({ ...prev, to }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <Button
            icon={BarsFilterIcon}
            text="Filtrar"
            size="normal"
            variation="outline"
            disabled={true}
          />
        </div>
      </HeaderTitle>

      <div
        className={clsx(
          styles.dashboardBody,
          { [styles.mockedData]: !hasFiles }
        )}
      >
        <div className={styles.financeContent}>
          {(hasFiles === undefined || (hasFiles === true && (finance === null || education === null))) ? (
            <div className={styles.loadingWrapper}>
              <img src={loadingGif} alt="Carregando..." />
            </div>
          ) : hasFiles && finance?.financialData?.totalRevenue === 0
            && (finance.financialData?.totalBilledInPeriod ?? 0) === 0
            && (finance.financialData?.totalReceivedInPeriod ?? 0) === 0
            && (finance.financialData?.delinquencyRate?.delinquencyValue ?? 0) === 0
            && (finance.futureEntries?.length ?? 0) === 0
            && (finance.monthlyDelinquencyRates?.length ?? 0) === 0
            && (finance.agreements?.length ?? 0) === 0 ? (
            <ZeroData>
              <div>
                <h3>Sem dados para o período</h3>
                <p>
                  Não encontramos dados para o período filtrado. Altere as datas ou importe dados para esse período{" "}
                  <span 
                    onClick={() => navigate("/app/importar-dados")} 
                    style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
                  >
                    na página de importação de dados
                  </span>.
                </p>
              </div>
            </ZeroData>
          ) : hasFiles ? (
            <>
              <div className={styles.grid_5}>
                <FinanceCard
                  title="Receitas"
                  value={formatNumberToCurrency(
                    finance.financialData.totalRevenue
                  )}
                  icon={ChartMixedDownCircleDollarIcon}
                  explanation={"A receita é calculada somando todos os valores recebidos de todas as fontes."}
                  code="F-01"
                />

                <FinanceCard
                  title="Descontos"
                  value={formatNumberToCurrency(
                    finance.financialData.totalExpenses
                  )}
                  icon={ChartMixedUpCircleDollarIcon}
                  explanation={"Os descontos somam os descontos nas mensalidades, custos extras e descontos em outras receitas."}
                  code="F-02"
                />

                <FinanceCard
                  title="Acréscimos"
                  value={formatNumberToCurrency(finance.financialData.additions)}
                  icon={ChartMixedUpCircleDollarIcon}
                  explanation={"Acrescimentos representam valores adicionais somados ao total recebido, incluindo descontos concedidos e valores extras cobrados, como taxas ou acréscimos financeiros."}
                  code="F-03"
                />
                <FinanceCard
                  title="Ticket Médio Mensal"
                  value={formatNumberToCurrency(finance.averageTicket)}
                  icon={EnvelopeOpenDollarIcon}
                  explanation={"O Ticket Médio Mensal é calculado dividindo o valor total recebido em mensalidades pelo número de boletos pagos, mostrando quanto, em média, foi pago por boleto."}
                  code="F-04"
                />
              </div>
              <div className={styles.grid_1}>
                <FinanceInvoicing
                  received={finance.received}
                  billed={finance.billed}
                  proportionReceivedIssued={finance.proportionReceivedIssuedPct}
                  code="F-05"
                />
              </div>
              <div className={styles.grid_5}>
                <FinanceCard
                  title="Total de Alunos Matriculados"
                  value={education?.totalEnrolledStudents ?? "Sem dados"}
                  icon={ChartMixedDownCircleDollarIcon}
                  explanation={"Quantidade de estudantes diferentes que realizaram matrícula na instituição."}
                  code="E-A1"
                />
                <FinanceCard
                  title="Mensalidades Inadimplêntes (% - Qtd)"
                  value={addPorcentage(finance.financialData.delinquencyRate.installmentDelinquency, "ano", "anos")}
                  icon={ChartUserSearchIcon}
                  explanation={"A Taxa mensalidades de Inadimplência é calculada dividindo o total de parcelas vencidas e não pagas pelo total de parcelas do período, e multiplicando por 100 para obter o percentual."}
                  dataLink={buildDataLinkFromFilters("DelinquencyValue")}
                  code="F-06"
                  detailData={(finance.delinquencyBreakdowns || []).map(b => ({
                    year: b.year,
                    semester: b.semester,
                    month: b.month,
                    value: addPorcentage(b.installmentDelinquency)
                  }))}
                />
                <FinanceCard
                  title="Valor Inadimplente"
                  value={formatNumberToCurrency(finance.financialData.delinquencyRate.delinquencyValue)}
                  icon={ChartUserSearchIcon}
                  explanation={"O valor de inadimplência representa quanto do valor total a receber, no período filtrado, está vinculado a alunos que não efetuaram o pagamento após a data de vencimento."}
                  dataLink={buildDataLinkFromFilters("DelinquencyValue")}
                  code="F-07"
                  detailData={(finance.delinquencyBreakdowns || []).map(b => ({
                    year: b.year,
                    semester: b.semester,
                    month: b.month,
                    value: formatNumberToCurrency(b.delinquencyValue)
                  }))}
                />
                <FinanceCard
                  title="Taxa de Inadimplência (R$)"
                  value={addPorcentage(finance.financialData.delinquencyRate.overdueValuePercentage, "ano", "anos")}
                  icon={ChartUserSearchIcon}
                  explanation={"A Taxa de inadimplência representa quantos porcento do valor total a receber, no período filtrado, está vinculado a alunos que ainda não efetuaram o pagamento após a data de vencimento."}
                  dataLink={buildDataLinkFromFilters("DelinquencyValue")}
                  code="F-08"
                  detailData={(finance.delinquencyBreakdowns || []).map(b => ({
                    year: b.year,
                    semester: b.semester,
                    month: b.month,
                    value: addPorcentage(b.overdueValuePercentage)
                  }))}
                />
              </div>
              <div className={styles.grid_1}>
                <FinanceDeliquency delinquencies={finance.monthlyDelinquencyRates} code="F-09" />
              </div>

              {/* Tabela de Ticket Médio por Curso */}
              {(finance.disciplineIndex && finance.disciplineIndex.length > 0) && (
                <div className={styles.grid_1}>
                  <TicketMedioCursoTable 
                    disciplineData={finance.disciplineIndex} 
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div className={styles.grid_2}>
                <FinanceCard
                  title="Receitas"
                  value={formatNumberToCurrency(
                    mockedFinance.financialData.totalRevenue
                  )}
                  icon={ChartMixedDownCircleDollarIcon}
                  explanation={"A receita é calculada somando os valores pagos em mensalidades e os valores recebidos de outras fontes."}
                  code="F-01"
                />

                <FinanceCard
                  title="Descontos"
                  value={formatNumberToCurrency(
                    mockedFinance.financialData.totalExpenses
                  )}
                  icon={ChartMixedUpCircleDollarIcon}
                  explanation={"Os descontos somam os descontos nas mensalidades, custos extras e descontos em outras receitas, refletindo os valores que reduzem a receita total da instituição."}
                  code="F-02"
                />

                <FinanceCard
                  title="Margem de Lucro"
                  value={addPorcentage(mockedFinance.financialData.profitMargin)}
                  icon={ChartMixedPorcentageIcon}
                />

              </div>
              <div className={styles.grid_1}>
                <FinanceInvoicing
                  received={mockedFinance.received}
                  billed={mockedFinance.billed}
                  proportionReceivedIssued={mockedFinance.proportionReceivedIssued}
                  code="F-05"
                />
              </div>
              <div className={styles.grid_2}>
                <FinanceCard
                  title="Total de Alunos Matriculados"
                  value={mockedEducation?.totalEnrolledStudents ?? "Sem dados"}
                  icon={ChartMixedDownCircleDollarIcon}
                  code="E-A1"
                />

                <FinanceCard
                  title="Inadimplência no periodo"
                  value={addPorcentage(mockedFinance.financialData.defaultRate, "ano", "anos")}
                  icon={ChartUserSearchIcon}
                  code="F-09"
                />
                <FinanceCard
                  title="Ticket Médio Mensal"
                  value={formatNumberToCurrency(mockedFinance.averageTicket)}
                  icon={EnvelopeOpenDollarIcon}
                  code="F-04"
                />
              </div>
              <div className={styles.grid_1}>
                <FinanceDeliquency delinquencies={mockedFinance.monthlyDelinquencyRates} code="F-09" />
              </div>

              {/* Card de Top Curso - Dados Mockados */}
              <div className={styles.grid_1}>
                <DisciplineIndexTable 
                  disciplineData={mockedDisciplineIndex} 
                />
              </div>

              {/* Tabela de Ticket Médio por Curso - Dados Mockados */}
              <div className={styles.grid_1}>
                <TicketMedioCursoTable 
                  disciplineData={mockedDisciplineIndex} 
                />
              </div>
            </>
          )}
        </div>
      </div>
      {
        hasFiles === false && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              {/* Ícone antes do título */}
              <img
                src={AcademicIcon}
                alt="Icone de Importar Planilha"
                className={styles.modalIcon}
              />

              <h2>Você ainda não importou os dados da sua instituição</h2>
              <p>
                Para que possamos exibir os principais indicadores da sua instituição,
                é necessário primeiro importar uma planilha com dados.
              </p>
              <div
                onClick={() => navigate("/app/importar-dados")}
                className={clsx(
                  styles.suggestionsItem,
                  styles.active,
                  styles.modalButton
                )}
              >
                <img src={DocumentArrowRight} alt="document-arrow-right" />
                Importar Planilha
              </div>
            </div>
          </div>

        )
      }
    </div>

  );
};

export default Dashboard;
